import { BaseTransformer } from "./base.transformers";
import {
    EstimateInfo as OldEstimateInfo,
    Estimate as OldEstimate,
    ProgressInfo,
    DeliveryInfo,
} from "@/models/estimate";
import { Estimate as NewEstimate } from "../entities/estimate";
import { SpaceTransformer } from "./space.transformer";
import { Space } from "../entities/space";
import { Overheads } from "../entities/overhead";
import { Delivery, DeliveryItem } from "../entities/delivery";
import { DeliveryItemsTransformer } from "./delivery-items.transformer";
import { DimensionValue, ReactiveDimensions } from "../entities/dimensions";
import { Pricing } from "../entities/pricing";
import { CalculationItem } from "@/models/calculate";
import { OverheadItemsTransformer } from "./overheads.transformers";

type EstimateTransformerToOldRequest = {
    estimate: NewEstimate;
    spaces: Space[];
    delivery: Delivery;
};

export const EstimateTransformer: BaseTransformer<
    OldEstimate,
    NewEstimate,
    EstimateTransformerToOldRequest
> = {
    toOld({ estimate, spaces, delivery }) {
        const info: OldEstimateInfo = {
            started: Date.now(),
            priceDate: new Date(estimate.priceDate) || new Date(),
            priceDateFormated: estimate.priceDateFormated || "",
            subtotal:
                estimate.subtotal
                    ?.getSystemPrice(estimate.dealTypeCode)
                    .getValue() || 0,
            estimatePriceWithoutExtra:
                estimate.estimatePriceWithoutExtra
                    ?.getSystemPrice(estimate.dealTypeCode)
                    .getValue() || 0,
            estimatePrice:
                estimate.estimatePrice
                    ?.getSystemPrice(estimate.dealTypeCode)
                    .getValue() || 0,
            code: estimate.code || "",
            estimateNo: estimate.estimateNo || "",
            trackCode: estimate.trackCode || "",
            title: estimate.title || "",
            client: estimate.client,
            managerCode: estimate.managerCode || null,
            estimatorCode: estimate.estimatorCode || "",
            estimatorName: estimate.estimatorName || "",
            dealTypeCode: estimate.dealTypeCode || "",
            installationPlaceCode: estimate.installationPlaceCode || "",
            installationUsageCode: estimate.installationUsageCode || "",
            installationPlaceTitle: null, // This field seems to be removed in new format
            installedSystemCode: estimate.installedSystemCode || "",
            usageCode: estimate.installationUsageCode || "",
            colorCode: estimate.colorCode || "",
            shippingAddress: estimate.client.shippingAddress || "",
            shippingZip: estimate.shippingZip || "",
            shippingState: estimate.shippingState || "",
            billingAddress: estimate.client.billingAddress || "",
            billingZip: estimate.client.billingZip || "",
            billingState: estimate.client.billingState || "",
            delivery: estimate.delivery || false,
            deliverySum: estimate.deliverySum || 0,
            deliveryType: estimate.deliveryType || "",
            isIncludeCombinedDeliveryInPrice:
                estimate.isIncludeCombinedDeliveryInPrice || false,
            isShippingAndBillingEqual:
                estimate.isShippingAndBillingEqual || false,
            totalGlassWeight: estimate.totalGlassWeight || 0,
            installation: estimate.installation || false,
            isHaflInstallation: estimate.isHalfInstallation || false,
            halfInstallation: estimate.halfInstallation || 0,
            installationSum: estimate.installationSum || 0,
            overChargeSum: estimate.overChargeSum || 0,
            halfInstallationWithDiscount:
                estimate.halfInstallationWithDiscount || 0,
            totalSquareFt: estimate.totalSquareFt?.toSquareFt().format() || "",
            priceBreakdownInQuote: estimate.priceBreakdownInQuote || false,
            totalWithoutTax: estimate.totalWithoutTax || 0,
            totalWithTax: estimate.totalWithTax || 0,
            taxPercent: estimate.taxPercent || 0,
            taxSum: estimate.taxSum || 0,
            taxLimit: estimate.taxLimit,
            taxStatus: estimate.taxStatus,
            isTaxExempt: estimate.isTaxExempt || false,
            customerNotice: estimate.customerNotice || "",
        };

        const progressInfo: ProgressInfo = {
            status: estimate.progress.status || "",
            updated: estimate.progress.updated || "",
            validatedPage: estimate.progress.validatedPage || 1,
            activePage: estimate.progress.activePage || 1,
            activeSpaceCode: estimate.progress.activeSpaceCode || "",
            activeStructureCode: estimate.progress.activeStructureCode || "",
            activeDoorCode: estimate.progress.activeDoorCode || "",
            activeFillerCode: estimate.progress.activeFillerCode || "",
            isSaved: estimate.progress.isSaved || false,
        };

        // Create delivery info with empty arrays since they are not present in new format
        const deliveryInfo: DeliveryInfo = {
            deliveryAddress: {
                state: estimate.shippingState || "",
                zip: estimate.shippingZip || "",
                address: estimate.client.shippingAddress || "",
            },
            deliveryCosts: [],
            // deliveryCosts: deliveryItems.map(DeliveryItemsTransformer.toOld),
            extraCosts: [],
            multiplicator: estimate.multiplicator || 1,
            discountPercent: estimate.discountPercent ?? 0,
            discountSum: estimate.discountSum ?? 0,
        };

        const estimateLevelOverheads = spaces
            .map((space) => space.getOverheads())
            .find((overheads): overheads is Overheads => overheads != null);

        // Construct the final old format estimate
        return {
            overhead: estimateLevelOverheads
                ? OverheadItemsTransformer.toOld(estimateLevelOverheads)
                : [],
            spaces: spaces.map(SpaceTransformer.toOld),
            info,
            delivery: deliveryInfo,
            progress: progressInfo,
        };
    },
    fromOld(estimate: OldEstimate, calculations: CalculationItem): NewEstimate {
        const estimateInfo = estimate.info;
        const estimateProgress = estimate.progress;
        const deliveryInfo = estimate.delivery;

        return {
            title: estimateInfo.title || "",
            code: estimateInfo.code || "",
            started: estimateInfo.started || new Date(),
            priceDate: estimateInfo.priceDate || Date.now(),
            priceDateFormated: estimateInfo.priceDateFormated || "",
            dealTypeCode: estimateInfo.dealTypeCode || "",
            installationPlaceCode: estimateInfo.installationPlaceCode || "",
            installationUsageCode: estimateInfo.installationUsageCode || "",
            installedSystemCode: estimateInfo.installedSystemCode || "",
            trackCode: estimateInfo.trackCode || "",
            estimateNo: estimateInfo.estimateNo || "",
            colorCode: estimateInfo.colorCode || "",
            delivery: estimateInfo.delivery || false,
            deliveryType: estimateInfo.deliveryType || "",
            deliverySum: estimateInfo.deliverySum || 0,
            isIncludeCombinedDeliveryInPrice:
                estimateInfo.isIncludeCombinedDeliveryInPrice || false,
            isShippingAndBillingEqual:
                estimateInfo.isShippingAndBillingEqual || false,
            totalGlassWeight: estimateInfo.totalGlassWeight || 0,
            installation: estimateInfo.installation || false,
            shippingZip: estimateInfo.shippingZip || "",
            shippingState: estimateInfo.shippingState || "",
            // billingZip: estimateInfo.billingZip || "",
            // billingState: estimateInfo.billingState || "",
            managerCode: estimateInfo.managerCode || null,
            estimatorCode: estimateInfo.estimatorCode || "",
            estimatorName: estimateInfo.estimatorName || "",
            client: {
                name: estimateInfo.client.name || "",
                businessName: estimateInfo.client.businessName || "",
                email: estimateInfo.client.email || "",
                phone: estimateInfo.client.phone || "",
                shippingAddress: estimateInfo.client.shippingAddress || "",
                billingAddress: estimateInfo.client.billingAddress || "",
                billingZip: estimateInfo.client.billingZip || "",
                billingState: estimateInfo.client.billingState || "",
            },
            progress: {
                status: estimateProgress.status || "",
                updated: estimateProgress.updated || "",
                validatedPage: estimateProgress.validatedPage || 1,
                activePage: estimateProgress.activePage || 1,
                activeSpaceCode: estimateProgress.activeSpaceCode || "",
                activeStructureCode: estimateProgress.activeStructureCode || "",
                activeDoorCode: estimateProgress.activeDoorCode || "",
                activeFillerCode: estimateProgress.activeFillerCode || "",
                isSaved: estimateProgress.isSaved || false,
            },
            multiplicator: deliveryInfo.multiplicator,
            discountPercent: deliveryInfo.discountPercent,
            discountSum: deliveryInfo.discountSum,
            halfInstallation: estimateInfo.halfInstallation,
            installationSum: estimateInfo.installationSum,
            overChargeSum: estimateInfo.overChargeSum,
            halfInstallationWithDiscount:
                estimateInfo.halfInstallationWithDiscount,
            isHalfInstallation: estimateInfo.isHaflInstallation,
            totalSquareFt: new DimensionValue(0) || null,
            subtotal:
                new Pricing(
                    estimateInfo.subtotal,
                    estimateInfo.subtotal,
                    estimateInfo.subtotal,
                ) || new Pricing(0, 0, 0),
            estimatePrice: new Pricing(
                calculations.price,
                calculations.priceB2C,
                calculations.priceB2B,
            ),
            estimatePriceWithoutExtra: new Pricing(
                calculations.price,
                calculations.priceB2C,
                calculations.priceB2B,
            ),
            priceBreakdownInQuote: estimateInfo.priceBreakdownInQuote || false,
            totalWithoutTax: estimateInfo.totalWithoutTax || 0,
            totalWithTax: estimateInfo.totalWithTax || 0,
            taxPercent: estimateInfo.taxPercent || 0,
            taxSum: estimateInfo.taxPercent || 0,
            taxLimit: estimateInfo.taxLimit,
            taxStatus: estimateInfo.taxStatus,
            isTaxExempt: estimateInfo.isTaxExempt || false,
            customerNotice: estimateInfo.customerNotice || "",
        };
    },
};
